import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '../../../components/Loader/Loader';
import { useModal } from '../../../components/Modal/useModal';
import SellerManualUploadInstructionBlock from '../../../components/SellerManualUploadInstructionBlock/SellerManualUploadInstructionBlock';
import { PaymentConfirmationFile } from '../../../core/types';
import PageLayout from '../../../wrappers/PageLayout';
import SellerFilingFilesModal from './SellerFilingFilesModal';
import PaymentInstructionModal from '../../../components/PaymentInstructionModal/PaymentInstructionModal';
import SellerOngoingFiling from './SellerOngoingFiling';
import SellerCompletedFiling from './SellerCompletedFiling';
import Tabs from '../../../components/Tabs/Tabs';
import { useAppSelector, useQueriesTyped } from '../../../hooks';
import { getCompletedFiling, getOngoingFiling } from '../../../core/api';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import AgendaModal from '../../../components/AgendaModal/AgendaModal';
import usePaymentInstructionModal from './usePaymentInstructionModal';
import useAgendaModal from './useAgendaModal';
import './SellerFiling.scss';

export type TabType = 'Current month' | 'Past periods';
const TABS: TabType[] = ['Current month', 'Past periods'];

export default function SellerFiling() {
  let location = useLocation();
  const params = new URLSearchParams(location.search);
  const companyIdParam = params.get('tab');

  const getTabFromQueryString = () => {
    if (companyIdParam === 'current_month') {
      return 'Current month';
    }
    if (companyIdParam === 'past_periods') {
      return 'Past periods';
    }

    return undefined;
  };

  const user = useAppSelector((s) => s.user.user);
  const activeCompany = useAppSelector((s) => s.user.activeCompany);

  const [ongoing, completed] = useQueriesTyped([
    {
      queryKey: ['SellerOngoingFiling', user!.Id, activeCompany?.company_id],
      queryFn: () => getOngoingFiling(activeCompany?.company_id),
    },
    {
      queryKey: ['SellerCompletedFiling', user!.Id, activeCompany?.company_id],
      queryFn: () => getCompletedFiling(activeCompany?.company_id),
    },
  ]);

  const [filesModalData, setFilesModalData] = useState<
    | {
        files: PaymentConfirmationFile[];
        date: Date;
        comment?: string;
      }
    | undefined
  >(undefined);

  const isLoading = ongoing.isLoading || completed.isLoading;

  const { t } = useTranslation();

  const {
    isModalOpened: isFilesModalOpened,
    closeModal: closeFilesModal,
    openModal: openFilesModal,
  } = useModal();

  const {
    onClickConfirmPayment,
    isPaymentInstructionModalOpened,
    closePaymentInstructionModal,
    reportToConfirmPayment,
    handleOpenPaymentInstructionModal,
    isConfirmingPayment,
  } = usePaymentInstructionModal();

  const {
    isAgendaModalOpened,
    closeAgendaModal,
    handleOpenAgendaModal,
    onClickConfirmAgenda,
    isConfirmingAgenda,
    reportToConfirmAgenda,
  } = useAgendaModal();

  const onFolderBtnClick =
    (files: PaymentConfirmationFile[], date: Date, comment?: string) => () => {
      setFilesModalData({ files, date, comment });
      openFilesModal();
    };

  const renderContent = (activeTab: TabType) => {
    switch (activeTab) {
      case 'Past periods':
        return (
          <SellerCompletedFiling
            reports={completed.data || []}
            onAmountBtnClick={handleOpenPaymentInstructionModal}
            onFolderBtnClick={onFolderBtnClick}
            onClickAgenda={handleOpenAgendaModal}
          />
        );

      case 'Current month':
      default:
        return (
          <SellerOngoingFiling
            reports={ongoing.data || []}
            onAmountBtnClick={handleOpenPaymentInstructionModal}
            onFolderBtnClick={onFolderBtnClick}
            onClickAgenda={handleOpenAgendaModal}
          />
        );
    }
  };

  if (isLoading) return <Loader />;

  return (
    <PageLayout
      className='flex-grow-1'
      rightColumn={
        activeCompany?.ManualUploadNeeded ? (
          <div style={{ maxWidth: 328 }} className='ms-lg-auto pt-3 pt-lg-0'>
            <SellerManualUploadInstructionBlock />
          </div>
        ) : undefined
      }
    >
      <div
        className={classNames('Paper px-4 py-3', {
          'mx-auto Paper--lg': !activeCompany?.ManualUploadNeeded,
        })}
      >
        <div className='Text--30 fw-900 mb-3'>{t('Filing')}</div>
        <Tabs
          tabs={TABS}
          renderContent={renderContent}
          initialTab={getTabFromQueryString()}
        />
        <SellerFilingFilesModal
          isModalOpened={isFilesModalOpened}
          closeModal={closeFilesModal}
          files={filesModalData?.files || []}
          date={filesModalData?.date}
          comment={filesModalData?.comment}
        />
        <PaymentInstructionModal
          report={reportToConfirmPayment}
          isModalOpened={isPaymentInstructionModalOpened}
          closeModal={closePaymentInstructionModal}
          onClickConfirm={onClickConfirmPayment(reportToConfirmPayment)}
          isConfirming={isConfirmingPayment}
        />
        <AgendaModal
          report={reportToConfirmAgenda}
          isModalOpened={isAgendaModalOpened}
          closeModal={closeAgendaModal}
          onClickConfirm={onClickConfirmAgenda(reportToConfirmAgenda)}
          isConfirming={isConfirmingAgenda}
        />
      </div>
    </PageLayout>
  );
}
